import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckSquare, Square, Calendar, Home, ClipboardList,
  AlertCircle, Clock, CalendarDays, ArrowRight, Hourglass,
} from "lucide-react";
import http from "@/lib/http";
import { parseDate } from "@/lib/format";

const VISIBLE_LIMIT = 8;

/**
 * Compact tasks panel on the Dashboard.
 * Reuses `/api/tasks` which is already scoped to the current user's
 * postcodes_makelaar ∪ postcodes_particulier (and standalone tasks created
 * by the user).
 */
export default function DashboardTasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    try {
      const r = await http.get("/api/tasks");
      setTasks(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (task) => {
    await http.patch(`/api/tasks/${task.id}`, { completed: !task.completed });
    await load();
  };

  const buckets = useMemo(() => {
    const today = startOfDay(new Date());
    const out = { overdue: [], today: [], planned: [] };
    for (const t of tasks) {
      if (t.completed) continue;
      if (!t.due_date) { out.planned.push(t); continue; }
      const parsed = parseDate(t.due_date);
      if (!parsed) { out.planned.push(t); continue; }
      const d = startOfDay(parsed);
      if (d < today) out.overdue.push(t);
      else if (d.getTime() === today.getTime()) out.today.push(t);
      else out.planned.push(t);
    }
    return out;
  }, [tasks]);

  const totalOpen = buckets.overdue.length + buckets.today.length + buckets.planned.length;

  // Flatten in display order: overdue → today → planned, optionally truncated
  const visible = useMemo(() => {
    const ordered = [
      ...buckets.overdue.map(t => ({ ...t, _bucket: "overdue" })),
      ...buckets.today.map(t => ({ ...t, _bucket: "today" })),
      ...buckets.planned.map(t => ({ ...t, _bucket: "planned" })),
    ];
    return showAll ? ordered : ordered.slice(0, VISIBLE_LIMIT);
  }, [buckets, showAll]);

  return (
    <section data-testid="dashboard-tasks-section">
      <div className="flex items-end justify-between mb-1">
        <h2 className="font-display text-[22px] text-[var(--ink-900)]">Open taken</h2>
        <button type="button" onClick={() => navigate("/tasks")}
                data-testid="dashboard-tasks-view-all"
                className="text-[12px] text-[var(--teal-700)] hover:text-[var(--teal-900)] flex items-center gap-1">
          Alle taken <ArrowRight size={12} />
        </button>
      </div>
      <p className="text-[13px] text-[var(--ink-500)] mb-4">
        Taken voor de postcodes waar je toegang tot hebt.
      </p>

      <div className="card p-0 overflow-hidden" data-testid="dashboard-tasks-list">
        {loading ? (
          <div className="p-6 text-sm text-[var(--ink-500)]">Laden…</div>
        ) : totalOpen === 0 ? (
          <div className="p-6 text-sm text-[var(--ink-500)] flex items-center gap-2"
               data-testid="dashboard-tasks-empty">
            <ClipboardList size={16} className="text-[var(--ink-400)]" />
            Geen open taken. Tijd voor een koffie.
          </div>
        ) : (
          <>
            {/* Summary chips */}
            <div className="flex flex-wrap items-center gap-2 px-5 pt-3 pb-2 border-b border-[var(--border-soft)]"
                 data-testid="dashboard-tasks-summary">
              {buckets.overdue.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] text-[var(--danger,#D6646E)]
                                 bg-[#FBE7E7] border border-[#EFBABA] rounded-full px-2 py-0.5">
                  <AlertCircle size={11} /> {buckets.overdue.length} over tijd
                </span>
              )}
              {buckets.today.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] text-[#92400E]
                                 bg-[#FEF3C7] border border-[#FDE68A] rounded-full px-2 py-0.5">
                  <Clock size={11} /> {buckets.today.length} vandaag
                </span>
              )}
              {buckets.planned.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] text-[var(--ink-700)]
                                 bg-[var(--sand-100)] border border-[var(--border-soft)] rounded-full px-2 py-0.5">
                  <CalendarDays size={11} /> {buckets.planned.length} gepland
                </span>
              )}
            </div>
            <ul>
              {visible.map(task => (
                <DashboardTaskRow
                  key={task.id}
                  task={task}
                  bucket={task._bucket}
                  onToggle={() => toggle(task)}
                  onOpenLead={(id) => id && navigate(`/lead/${id}`)}
                  onOpenFollow={(id) => id && navigate(`/follow-through/${id}`)}
                />
              ))}
            </ul>
            {totalOpen > VISIBLE_LIMIT && !showAll && (
              <button type="button" onClick={() => setShowAll(true)}
                      data-testid="dashboard-tasks-show-more"
                      className="w-full py-2.5 text-[12px] text-[var(--teal-700)] hover:bg-[var(--sand-50)]
                                 border-t border-[var(--border-soft)] transition-colors">
                Toon alle {totalOpen} taken
              </button>
            )}
            {showAll && totalOpen > VISIBLE_LIMIT && (
              <button type="button" onClick={() => setShowAll(false)}
                      className="w-full py-2.5 text-[12px] text-[var(--ink-500)] hover:bg-[var(--sand-50)]
                                 border-t border-[var(--border-soft)] transition-colors">
                Toon minder
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function DashboardTaskRow({ task, bucket, onToggle, onOpenLead, onOpenFollow }) {
  const dueColor = dueDateColor(task.due_date, task.completed, bucket);
  const isDone = !!task.completed;
  const leadLabel = task.lead
    ? `${task.lead.address || ""}${task.lead.postal_code ? " · " + task.lead.postal_code : ""}${task.lead.city ? " " + task.lead.city : ""}`
    : null;
  const followLabel = task.follow_through
    ? `${task.follow_through.address || ""}${task.follow_through.postal_code ? " · " + task.follow_through.postal_code : ""}`
    : null;
  const creatorName = task.created_by?.name;
  const accent =
    bucket === "overdue" ? "border-l-[3px] border-l-[var(--danger,#D6646E)]"
    : bucket === "today" ? "border-l-[3px] border-l-[#D97706]"
    : "border-l-[3px] border-l-transparent";
  return (
    <li data-testid={`dashboard-task-${task.id}`}
        className={`flex items-start gap-3 px-5 py-3 border-b border-[var(--border-soft)] last:border-b-0
                    hover:bg-[var(--sand-50)] transition-colors ${accent}`}>
      <button type="button" onClick={onToggle}
              data-testid={`dashboard-task-toggle-${task.id}`}
              className={`mt-0.5 transition-colors ${isDone ? "text-[var(--teal-600)]" : "text-[var(--ink-400)] hover:text-[var(--teal-600)]"}`}>
        {isDone ? <CheckSquare size={17} /> : <Square size={17} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] font-medium ${isDone ? "line-through text-[var(--ink-500)]" : "text-[var(--ink-900)]"}`}>
          {task.title}
        </div>
        <div className="text-[11px] text-[var(--ink-500)] mt-0.5 flex items-center gap-2 flex-wrap">
          {leadLabel && (
            <button type="button" onClick={() => onOpenLead(task.lead_id)}
                    data-testid={`dashboard-task-lead-${task.id}`}
                    className="hover:text-[var(--teal-700)] flex items-center gap-1 truncate">
              <Home size={10} /> {leadLabel}
            </button>
          )}
          {followLabel && (
            <button type="button" onClick={() => onOpenFollow(task.follow_through_id)}
                    data-testid={`dashboard-task-follow-${task.id}`}
                    className="hover:text-[var(--teal-700)] flex items-center gap-1 truncate">
              <Hourglass size={10} /> {followLabel}
            </button>
          )}
          {creatorName && (
            <>
              {(leadLabel || followLabel) && <span className="text-[var(--ink-300)]">·</span>}
              <span>door {creatorName}</span>
            </>
          )}
        </div>
      </div>
      {task.due_date && (
        <div className={`text-[11px] font-medium tabular shrink-0 flex items-center gap-1 ${dueColor}`}
             data-testid={`dashboard-task-due-${task.id}`}>
          <Calendar size={11} />
          {formatDue(task.due_date)}
        </div>
      )}
    </li>
  );
}

// ---------- date helpers ----------
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function dueDateColor(due_date, completed, bucket) {
  if (completed) return "text-[var(--ink-400)]";
  if (bucket === "overdue") return "text-[var(--danger,#D6646E)]";
  if (bucket === "today") return "text-[#D97706]";
  if (!due_date) return "text-[var(--ink-500)]";
  return "text-[var(--ink-500)]";
}

function formatDue(due_date) {
  if (!due_date) return "";
  const today = startOfDay(new Date());
  const parsed = parseDate(due_date);
  if (!parsed) return "—";
  const d = startOfDay(parsed);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Vandaag";
  if (diff === 1) return "Morgen";
  if (diff === -1) return "Gisteren";
  if (diff < 0) return `${Math.abs(diff)}d te laat`;
  if (diff < 7) return `+${diff}d`;
  return parsed.toLocaleDateString("nl-BE", { day: "2-digit", month: "short" });
}
